import React, { useState } from "react";
import {
  Layers,
  AlertCircle,
  Clock,
  Banknote,
  FileText,
  Users,
  Plus,
} from "lucide-react";
import {
  useDashboardOverview,
  useDashboardServiceOrders,
  useDashboardSlaPerformance,
  useDashboardSopBySource,
  useDashboardSopCost,
} from "./hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
// import ServiceOrdersTable from "./components/ServiceOrdersTable";

import { ServiceOrdersChart } from "./components/ServiceOrdersChart";
import { SLAPerformanceChart } from "./components/SLAPerformanceChart";
import { ServiceSourcesChart } from "./components/ServiceSourcesChart";
import { MonthlyCostChart } from "./components/MonthlyCostChart";
import { QuickActions } from "@shared/components/QuickActions";
import { KPICard } from "./components/KPICard";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";
import { useNavigate } from "react-router-dom";
import { logEvent } from "@shared/analytics/analytics";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { useGetDashboardData } from "@shared/api/hooks";

const ServiceOrderDashboard: React.FC = () => {
  const remote_utility_id = getRemoteUtilityId();
  const currency = useCurrency();
  usePageViewTimer({ pageName: "WX Dashboard" });

  // Lift timePeriod state to parent component
  const [timePeriod, setTimePeriod] = useState("30");
  const [sopBySourceTimePeriod, setSopBySourceTimePeriod] = useState("30");

  // API Hooks - now passing days parameter
  // const { data: overviewData, isLoading: overviewLoading } =
  //   useDashboardOverview({ remote_utility_id });
  // const { data: serviceOrdersData, isLoading: serviceOrdersLoading } =
  //   useDashboardServiceOrders({
  //     remote_utility_id,
  //     days: timePeriod,
  //   });
  // const { data: slaData, isLoading: slaLoading } = useDashboardSlaPerformance({
  //   remote_utility_id,
  // });
  // const { data: sopSourceData, isLoading: sopSourceLoading } =
  //   useDashboardSopBySource({
  //     remote_utility_id,
  //     days: sopBySourceTimePeriod,
  //   });
  // const { data: sopCostData, isLoading: sopCostLoading } = useDashboardSopCost({
  //   remote_utility_id,
  // });
  const { data: wxDashboard } = useGetDashboardData("service_order_dashboard", {
    days: timePeriod,
  });
  const overviewData = wxDashboard?.metrics?.dashboardOverview;
  const serviceOrdersData = wxDashboard?.metrics?.dashboardServiceOrders;
  const slaData = wxDashboard?.metrics?.dashboardSlaPerformance;
  const sopSourceData = wxDashboard?.metrics?.dashboardSopBySource;
  const sopCostData = wxDashboard?.metrics?.dashboardSopCost;
  const kpiData = [
    {
      title: "Total Service Orders",
      count: overviewData?.totalServiceOrders,
      trend: overviewData?.totalServiceOrdersPctChange,
      trendUp: true,
      icon: <Layers />,
      trendDurationDisplay: "from last month",
    },
    {
      title: "Overdue Service Orders",
      count: overviewData?.overdueJobs,
      trend: overviewData?.overdueJobsPctChange,
      trendUp: false,
      icon: <AlertCircle />,
      trendDurationDisplay: "from last month",
    },
    {
      title: "Avg Resolution Time",
      count: overviewData?.avgResolutionHours,
      trend: overviewData?.avgResolutionPctChange,
      trendUp: false,
      icon: <Clock />,
      trendDurationDisplay: "from last month",
    },
    {
      title: "Total Cost",
      count: `${currency} ${overviewData?.totalCost}`,
      trend: overviewData?.totalCostPctChange,
      trendUp: true,
      icon: <Banknote />,
      trendDurationDisplay: "from last month",
    },
  ];

  const navigate = useNavigate();

  const handleNavigate = (route: string) => {
    logEvent(`${route.split("/").at(-1)} Action Clicked`);
    navigate(route);
  };

  const actions = [
    {
      title: "Create SOP",
      icon: FileText,
      color: "bg-blue-50 hover:bg-blue-100",
      description: "Standard Operating Procedure",
      onClick: () => handleNavigate("/service-orders/wx-master/create"),
    },
    {
      title: "View Field Technicians",
      icon: Users,
      color: "bg-green-50 hover:bg-green-100",
      description: "Technician Management",
      onClick: () => handleNavigate("/service-orders/field-force/"),
    },
    {
      title: "Create Service Order",
      icon: Plus,
      color: "bg-orange-50 hover:bg-orange-100",
      description: "New Service Request",
      onClick: () =>
        handleNavigate("/service-orders/wx-dashboard/create-service-order"),
    },
    {
      title: "Bulk Assign",
      icon: Layers,
      color: "bg-purple-50 hover:bg-purple-100",
      description: "Mass Assignment Tool",
      onClick: () => handleNavigate("/service-orders/dispatcher"),
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div>
        <h1 className="page-title">Service Order Dashboard</h1>
        <p className="page-subtitle">
          Enterprise field service operations management & real-time analytics
        </p>
      </div>

      <div className="pt-4 space-y-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {kpiData.map((kpi, index) => (
            // <KPICard key={index} {...kpi} />
            <QuickStatsCard {...kpi} />
          ))}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <ServiceOrdersChart
            serviceOrdersData={serviceOrdersData}
            timePeriod={timePeriod}
            onTimePeriodChange={setTimePeriod}
          />
          <SLAPerformanceChart slaData={slaData} />
          <ServiceSourcesChart
            sopSourceData={sopSourceData}
            timePeriod={sopBySourceTimePeriod}
            onTimePeriodChange={setSopBySourceTimePeriod}
          />
          <MonthlyCostChart sopCostData={sopCostData} />
        </div>

        {/* Service Orders Table */}
        {/* <ServiceOrdersTable /> */}

        {/* Quick Actions */}
        <QuickActions actions={actions} />
      </div>
    </div>
  );
};

export default ServiceOrderDashboard;
